export interface LegacyDraftLabAuth {
  version: 1;
  algorithm: "PBKDF2-SHA-256";
  iterations: 600000;
  salt: string;
  verifier: string;
}

export function decodeLegacyDraftLabBase64(
  value: unknown,
  expectedLength: number,
): Uint8Array<ArrayBuffer> | null {
  if (typeof value !== "string") return null;
  try {
    const decoded = globalThis.atob(value);
    if (decoded.length !== expectedLength) return null;
    return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
}

export function parseLegacyDraftLabAuth(raw: string | null): LegacyDraftLabAuth | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    const record = parsed as Record<string, unknown>;
    const keys = parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? Object.keys(record).sort()
      : [];
    const expectedKeys = ["algorithm", "iterations", "salt", "verifier", "version"];
    if (
      !parsed
      || typeof parsed !== "object"
      || Array.isArray(parsed)
      || keys.length !== expectedKeys.length
      || keys.some((key, index) => key !== expectedKeys[index])
      || record.version !== 1
      || record.algorithm !== "PBKDF2-SHA-256"
      || record.iterations !== 600000
      || !decodeLegacyDraftLabBase64(record.salt, 16)
      || !decodeLegacyDraftLabBase64(record.verifier, 32)
    ) return null;
    return parsed as LegacyDraftLabAuth;
  } catch {
    return null;
  }
}

export async function verifyLegacyDraftLabPassword(
  password: string,
  auth: LegacyDraftLabAuth,
): Promise<boolean> {
  if (password.length < 10 || password.length > 128) return false;
  const salt = decodeLegacyDraftLabBase64(auth.salt, 16);
  const expected = decodeLegacyDraftLabBase64(auth.verifier, 32);
  if (!salt || !expected || !globalThis.crypto?.subtle) return false;
  try {
    const material = await globalThis.crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(password),
      "PBKDF2",
      false,
      ["deriveBits"],
    );
    const derived = new Uint8Array(await globalThis.crypto.subtle.deriveBits({
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations: auth.iterations,
    }, material, 256));
    let difference = derived.length ^ expected.length;
    for (let index = 0; index < Math.max(derived.length, expected.length); index += 1) {
      difference |= (derived[index] ?? 0) ^ (expected[index] ?? 0);
    }
    return difference === 0;
  } catch {
    return false;
  }
}
